import { useState } from 'react';
import { hotTopics } from '@/mocks/home';
import Reveal from '@/components/feature/Reveal';

export default function TopicFlow() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [followed, setFollowed] = useState<Record<string, boolean>>({});

  const toggleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFollow = (id: string) => {
    setFollowed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="topics" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-medium tracking-widest text-primary-700">TOPICS / 正在热议</span>
              <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                大图话题流，一眼看清事情脉络
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600">
                每个话题都附上讨论重点与评论数，图片、观点、细节并排陈列，方便你挑一个直接进去围观。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600">
                共 {hotTopics.length} 个在聊话题
              </span>
            </div>
          </div>
        </Reveal>

        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {hotTopics.map((topic, index) => (
            <Reveal key={topic.id} delay={index * 60}>
              <article className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300">
                <div className="relative h-44 w-full overflow-hidden bg-background-200 md:h-48">
                  <img src={topic.image} alt={topic.title} className="h-full w-full object-cover object-top" />
                  <span className="absolute left-3 top-3 rounded-full bg-background-50/95 px-2.5 py-0.5 text-[11px] font-medium text-foreground-800">
                    {topic.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <h4 className="font-heading text-base font-bold leading-snug text-foreground-950">
                    <a href="#comments" className="cursor-pointer hover:text-primary-700">
                      {topic.title}
                    </a>
                  </h4>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-chat-3-line text-xs" />
                      </span>
                      {topic.comments}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-eye-line text-xs" />
                      </span>
                      {topic.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-user-line text-xs" />
                      </span>
                      {topic.author}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center gap-2 border-t border-background-200 pt-3">
                    <button
                      type="button"
                      onClick={() => toggleLike(topic.id)}
                      className={`flex cursor-pointer items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                        liked[topic.id]
                          ? 'border-accent-300 bg-accent-50 text-accent-700'
                          : 'border-background-300 bg-background-50 text-foreground-700 hover:border-accent-300'
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={`${liked[topic.id] ? 'ri-heart-fill' : 'ri-heart-line'} text-xs`} />
                      </span>
                      {liked[topic.id] ? '已赞同' : '赞同'}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleFollow(topic.id)}
                      className={`flex cursor-pointer items-center gap-1 rounded-md border px-3 py-1.5 text-xs transition-colors ${
                        followed[topic.id]
                          ? 'border-primary-300 bg-primary-50 text-primary-700'
                          : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={`${followed[topic.id] ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-xs`} />
                      </span>
                      {followed[topic.id] ? '已关注' : '关注'}
                    </button>
                    <a
                      href="#comments"
                      className="ml-auto flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-primary-700 hover:text-primary-800"
                    >
                      进评论
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}